import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useRef, useState } from 'react';
import slugify from 'react-slugify';
import Tag from '../../../../components/Display/Tag';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useToastContext } from '../../../../contexts/ToastContext';
import { privateFetch } from '../../../../utils/apiHelper';
import { CONSTANT } from '../../../../utils/constants';
import { getDateTimeStringOrUndefined } from '../../../../utils/dateHelper';
import { validateURL } from '../../../../utils/stringHelper';

const EMPTY_PROGRAM = { title: 'Aucun programme', id: undefined };

function EventManager({ event, eventManagerIsOpen, onCreate, isOpen, onUpdate, onClose, loading }) {
  const { accountContext } = useAccountContext();
  const { showToast } = useToastContext();
  const mainImageFileUploadRef = useRef(null);
  const secondaryImageFileUploadRef = useRef(null);
  const [imageLoading, setImageLoading] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [placeName, setPlaceName] = useState('');
  const [startDate, setStartDate] = useState(undefined);
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [mainImage, setMainImage] = useState(undefined);
  const [secondaryImage, setSecondaryImage] = useState(undefined);

  const [program, setProgram] = useState(undefined);
  const [programList, setProgramList] = useState([]);

  const [distributionList, setDistributionList] = useState([]);

  const [duration, setDuration] = useState(120);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description);
      setPlaceName(event.place_name);
      setStartDate(new Date(event.start_dt));
      setDuration((new Date(event.end_dt).getTime() - new Date(event.start_dt).getTime()) / 60000);
      setCtaLabel(event.cta_label);
      setCtaUrl(event.cta_url);
      setMainImage(event.main_image);
      setSecondaryImage(event.secondary_image);
      setProgram(event.program || EMPTY_PROGRAM);
    } else {
      setTitle('');
      setDescription('');
      setPlaceName('');
      setStartDate(new Date(moment().set({ hour: 20, minute: 0, second: 0, millisecond: 0 })));
      setDuration(120);
      setCtaLabel('Accéder à la billeterie');
      setCtaUrl('');
      setMainImage(undefined);
      setSecondaryImage(undefined);
      setProgram(undefined);
    }
  }, [event, eventManagerIsOpen]);

  useEffect(() => {
    fetchProgramList();
  }, [isOpen]);

  const fetchProgramList = async () => {
    await privateFetch('GET', 'melismes', `/cu/${accountContext.id}/program/`).then((res) => {
      setProgramList(res);
    });
  };

  const uploadMainImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setMainImage(res);
    });
    setImageLoading(false);
  };

  const uploadSecondaryImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setSecondaryImage(res);
    });
    setImageLoading(false);
  };

  const onUploadMainImage = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClearMainImage();
    } else {
      setImageLoading(true);
      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('name', slugify(file.name));
      imageForm.append('file', file);
      uploadMainImage(imageForm);
    }
  };

  const onClearMainImage = () => {
    setMainImage(undefined);
    mainImageFileUploadRef.current.clear();
  };

  const onUploadSecondaryImage = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClearSecondaryImage();
    } else {
      setImageLoading(true);
      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('name', slugify(file.name));
      imageForm.append('file', file);
      uploadSecondaryImage(imageForm);
    }
  };

  const onClearSecondaryImage = () => {
    setSecondaryImage(undefined);
    secondaryImageFileUploadRef.current.clear();
  };

  const handleProgramSelection = (program) => {
    setProgram(program);
    if (program?.id) {
      setTitle(program?.title);
      setMainImage(program?.secondary_image);
      setSecondaryImage(program?.main_image);
      setDistributionList(program?.distributions);
      setDescription(program?.description);
    }
    if (!program?.id) {
      setTitle('');
      setMainImage(undefined);
      setSecondaryImage(undefined);
      setDistributionList([]);
      setDescription('');
    }
  };

  const handleValidate = () => {
    const eventPayload = {
      title,
      description,
      place_name: placeName,
      start_dt: getDateTimeStringOrUndefined(startDate),
      end_dt: getDateTimeStringOrUndefined(startDate.setMinutes(startDate.getMinutes() + duration)),
      cta_url: ctaUrl,
      cta_label: ctaLabel,
      main_image: mainImage?.id,
      secondary_image: secondaryImage?.id,
      distributions: distributionList,
      program: program?.id
    };
    event ? onUpdate(eventPayload) : onCreate(eventPayload);
  };

  const formIsValid = () => {
    return (
      program && startDate && title.length > 3 && description.length > 3 && placeName.length > 3
    );
  };

  const dropdownTemplate = (option, props) => {
    if (!option) return <span>{props.placeholder}</span>;
    if (!option.id) {
      return <Tag tag={{ label: 'Ne lier à aucun programme', bgColor: 'bg-green-600' }} />;
    } else {
      return (
        <Tag
          tag={{
            label: option.title,
            bgColor: 'bg-slate-700',
            icon: 'program'
          }}
        />
      );
    }
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={event ? 'Valider la modification' : 'Valider la création'}
        className="w-full"
        disabled={!formIsValid()}
        onClick={handleValidate}
        severity="success"
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: 600 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${event ? 'Modifier' : 'Ajouter'} un évènement`}</h3>
      }>
      <div className="sidebar-content-container">
        <div className="form-wrapper">
          {!event && (
            <FormInput label={'Programme lié'}>
              <Dropdown
                id="program"
                value={program}
                onChange={(e) => handleProgramSelection(e.value)}
                options={[EMPTY_PROGRAM, ...programList]}
                optionLabel="title"
                placeholder="Veuillez sélectionner le programme lié"
                filter={true}
                filterInputAutoFocus={true}
                valueTemplate={dropdownTemplate}
                itemTemplate={dropdownTemplate}
                dataKey="id"
              />
            </FormInput>
          )}
          {program && (
            <>
              <FormInput label={"Nom de l'évènement"} required isValid={title.length > 3}>
                <InputText
                  id="title"
                  value={title}
                  maxLength={300}
                  placeholder="Veuillez saisir le nom de l'évènement"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </FormInput>
              <FormInput label={"Lieu de l'évènement"} required isValid={placeName.length > 3}>
                <InputText
                  id="place_name"
                  value={placeName}
                  maxLength={300}
                  placeholder="Veuillez saisir le nom du lieu"
                  onChange={(e) => setPlaceName(e.target.value)}
                />
              </FormInput>

              <div className="grid grid-cols-2 gap-3">
                <FormInput label={'Date de début'} required isValid={startDate}>
                  <Calendar
                    id="start_dt"
                    onChange={(e) => setStartDate(e.value)}
                    placeholder="Veuillez selectionner la date de début"
                    value={startDate}
                    locale="fr"
                    showTime
                    hourFormat="24"
                    stepMinute={15}
                  />
                </FormInput>

                <FormInput label={'Durée (en minutes)'} required isValid={duration}>
                  <Dropdown
                    id="duration"
                    value={duration}
                    onChange={(e) => setDuration(e.value)}
                    options={CONSTANT.melismes.eventDurations}
                    optionLabel="label"
                    placeholder="Veuillez sélectionner la durée de l'évènement"
                  />
                </FormInput>
              </div>

              <FormInput label={'Description'} required isValid={description.length > 3}>
                <RichTextEditor
                  value={description}
                  onChange={(value) => {
                    setDescription(value);
                  }}
                />
              </FormInput>

              <FormInput label={'Label du bouton'}>
                <InputText
                  id="cta_label"
                  value={ctaLabel}
                  maxLength={100}
                  placeholder="Veuillez saisir le texte du bouton"
                  onChange={(e) => setCtaLabel(e.target.value)}
                />
              </FormInput>

              <FormInput
                label={'Lien du bouton'}
                errorMessage={
                  !validateURL(ctaUrl) && "L'URL doit commencer par http:// ou https://"
                }>
                <InputText
                  id="cta_url"
                  value={ctaUrl}
                  maxLength={300}
                  placeholder="Veuillez saisir l'url du site de vente des tickets"
                  onChange={(e) => setCtaUrl(e.target.value)}
                  invalid={!validateURL(ctaUrl)}
                />
              </FormInput>

              <FormInput
                key={'main-file'}
                helpMessage={'10Mo max.'}
                label={`Image principale (max. 10Mo)`}
                required
                isValid={mainImage}>
                {mainImage && (
                  <img
                    src={mainImage.file}
                    className="w-2/3 object-cover"
                    alt="illustration principale du programme"
                  />
                )}
                <div className="flex flex-row gap-x-3 mt-3">
                  {mainImage && (
                    <Button
                      key={'delete-img'}
                      label={"Supprimer l'image"}
                      outlined
                      onClick={onClearMainImage}
                      severity="danger"
                    />
                  )}
                  {imageLoading ? (
                    <div className="w-[50px] h-[50px] p-1">
                      <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                    </div>
                  ) : (
                    <FileUpload
                      ref={mainImageFileUploadRef}
                      auto
                      disabled={mainImage}
                      mode="basic"
                      name="file"
                      accept="image/*"
                      customUpload
                      uploadHandler={onUploadMainImage}
                      chooseLabel="Depuis mon ordinateur"
                    />
                  )}
                </div>
              </FormInput>

              <FormInput
                key={'secondary-file'}
                helpMessage={'10Mo max.'}
                label={`Image secondaire (max. 10Mo)`}
                required
                isValid={secondaryImage}>
                {secondaryImage && (
                  <img
                    src={secondaryImage.file}
                    className="w-2/3  object-cover"
                    alt="illustration secondaire du programme"
                  />
                )}
                <div className="flex flex-row gap-x-3 mt-3">
                  {secondaryImage && (
                    <Button
                      key={'delete-img'}
                      label={"Supprimer l'image"}
                      outlined
                      onClick={onClearSecondaryImage}
                      severity="danger"
                    />
                  )}
                  {imageLoading ? (
                    <div className="w-[50px] h-[50px] p-1">
                      <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                    </div>
                  ) : (
                    <FileUpload
                      ref={secondaryImageFileUploadRef}
                      auto
                      disabled={secondaryImage}
                      mode="basic"
                      name="file"
                      accept="image/*"
                      customUpload
                      uploadHandler={onUploadSecondaryImage}
                      chooseLabel="Depuis mon ordinateur"
                    />
                  )}
                </div>
              </FormInput>
            </>
          )}
        </div>
        {footerButtons}
      </div>
    </Sidebar>
  );
}

export default EventManager;
